import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { flushSync } from "react-dom";

const STORAGE_KEY = "scrubbingTimeline";

/** Standard: kontinuierliche Timeline aktiv. Nur bei explizitem Ausschalten („0“) oder URL scrubbing=0 deaktiviert. */
function readFromStorage(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const urlParam = new URLSearchParams(window.location.search).get("scrubbing");
    if (urlParam === "0") return false;
    if (urlParam === "1") return true;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "0") return false;
    return true;
  } catch {
    return true;
  }
}

interface ScrubbingTimelineContextValue {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  /** True während Nutzer auf Timeline drückt und wischt – Karussell pausiert, nur Finger steuert. */
  isScrubbing: boolean;
  setScrubbing: (scrubbing: boolean) => void;
}

const ScrubbingTimelineContext =
  createContext<ScrubbingTimelineContextValue | null>(null);

export function ScrubbingTimelineProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [enabled, setEnabledState] = useState(readFromStorage);
  const [isScrubbing, setScrubbingState] = useState(false);

  const setEnabled = useCallback((value: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
    } catch {
      /* ignore */
    }
    setEnabledState(value);
  }, []);

  const setScrubbing = useCallback((scrubbing: boolean) => {
    flushSync(() => setScrubbingState(scrubbing));
  }, []);

  const value = useMemo(
    () => ({ enabled, setEnabled, isScrubbing, setScrubbing }),
    [enabled, setEnabled, isScrubbing, setScrubbing]
  );

  return (
    <ScrubbingTimelineContext.Provider value={value}>
      {children}
    </ScrubbingTimelineContext.Provider>
  );
}

export function useScrubbingTimeline(): ScrubbingTimelineContextValue | null {
  return useContext(ScrubbingTimelineContext);
}
